import { path, os, fs } from '../../lib/cep/node';
import { ns } from '../../../shared/shared';
import { csi } from '../../lib/utils/bolt';

/**
 * Folder names the AutoCut desktop app uses for `app.getPath("userData")`,
 * mirroring `initAppName()` in the electron app (`AutoCut` in production,
 * `AutoCutStaging` / `AutoCutDev` otherwise). Probed in this order when
 * looking for an existing install; writes always target `AutoCut`.
 */
const DESKTOP_APP_FOLDER_NAMES = ['AutoCut', 'AutoCutStaging', 'AutoCutDev'];

const DESKTOP_APP_DATA_FILE_NAME = 'autocut_data.json';

/**
 * Root folder that holds per-app data folders:
 * `~/Library/Application Support` (macOS) / `%APPDATA%` (Windows).
 *
 * `csi.getSystemPath("userData")` returns exactly the parent of electron's
 * `app.getPath("userData")`, which is what lets this panel share an identity
 * with the desktop app. It can throw (it reaches into `window.__adobe_cep__`),
 * so every fallback is spelled out.
 */
export const getAppDataRootFolderPath = (): string => {
  try {
    const systemUserData = csi.getSystemPath('userData');
    if (systemUserData) {
      return systemUserData;
    }
  } catch (error) {
    console.warn("[folder] getSystemPath('userData') failed", error);
  }

  if (os.platform() === 'darwin') {
    return path.join(os.homedir(), 'Library', 'Application Support');
  }

  const appData = window.cep_node?.process?.env?.APPDATA;
  if (appData) {
    return appData;
  }

  return path.join(os.homedir(), 'AppData', 'Roaming');
};

/**
 * Path of the desktop app's data file, or undefined when AutoCut is not
 * installed yet. Doubles as the "is AutoCut already there?" probe.
 */
export const findDesktopAppDataFilePath = (): string | undefined => {
  try {
    const root = getAppDataRootFolderPath();
    for (const folderName of DESKTOP_APP_FOLDER_NAMES) {
      const filePath = path.join(root, folderName, DESKTOP_APP_DATA_FILE_NAME);
      if (fs.existsSync(filePath)) {
        return filePath;
      }
    }
  } catch (error) {
    console.warn('[folder] findDesktopAppDataFilePath failed', error);
  }

  return undefined;
};

/**
 * Candidate locations of the installed desktop app itself, in probe order.
 *
 * Deliberately a different signal from `findDesktopAppDataFilePath()`: the data
 * folder is created by *this panel* when it seeds the PostHog id, so it exists
 * after a single panel open even when AutoCut was never installed. The app
 * bundle is only ever created by the installer.
 *
 * Each candidate points at a file *inside* the install rather than at the
 * folder, so a leftover empty directory does not read as installed. Paths match
 * the desktop app's own uninstall flow ("move AutoCut.app to the Trash" /
 * "delete the AutoCut folder from LocalAppData").
 *
 * Only the production `AutoCut` name is probed, not `AutoCutStaging` /
 * `AutoCutDev`: the installer we would download is the production build, so a
 * staging install on a QA machine must not suppress it.
 */
const getDesktopAppProbePaths = (): string[] => {
  const appName = DESKTOP_APP_FOLDER_NAMES[0];

  if (os.platform() === 'darwin') {
    return [
      path.join('/Applications', `${appName}.app`, 'Contents', 'Info.plist'),
      path.join(
        os.homedir(),
        'Applications',
        `${appName}.app`,
        'Contents',
        'Info.plist'
      ),
    ];
  }

  return [
    path.join(os.homedir(), 'AppData', 'Local', appName, `${appName}.exe`),
  ];
};

/**
 * Path of the installed desktop app, or undefined when AutoCut is not installed.
 * Never throws: a failed probe must fall through to the download, never block
 * it.
 */
export const findInstalledDesktopAppPath = (): string | undefined => {
  try {
    for (const probePath of getDesktopAppProbePaths()) {
      if (fs.existsSync(probePath)) {
        return probePath;
      }
    }
  } catch (error) {
    console.warn('[folder] findInstalledDesktopAppPath failed', error);
  }

  return undefined;
};

/**
 * Where we seed the shared PostHog id when AutoCut is not installed yet. Always
 * the production folder: the installer we launch is the production build.
 */
export const getDesktopAppDataFilePathForWrite = (): string =>
  path.join(
    getAppDataRootFolderPath(),
    DESKTOP_APP_FOLDER_NAMES[0],
    DESKTOP_APP_DATA_FILE_NAME
  );

/**
 * Our own folder. Deliberately not the extension folder (root-owned for a
 * system-wide ZXP, and wiped on every extension update, which would re-fire the
 * "installed" event) and not `os.tmpdir()` (wiped by the OS, same problem).
 */
export const getExchangeDataFolderPath = () =>
  path.join(getAppDataRootFolderPath(), ns);

export const getExchangeDataFilePath = () =>
  path.join(getExchangeDataFolderPath(), 'analytics.json');

export const getTempFolderPath = () => {
  const osTempFolder = os.tmpdir();
  const tempFolder = path.join(osTempFolder, 'com.autocut.installer.com');

  if (!fs.existsSync(tempFolder)) {
    fs.mkdirSync(tempFolder, { recursive: true });
  }
  return tempFolder;
};
